/**
 * The monetization surfaces: the paywall, the limit sheet, mistakes and the
 * pronunciation clinic (docs/04-screen-specs.md E2–E3, G1, G6).
 *
 * Mistakes and the clinic sit in this file on purpose. They are what a learner is sent
 * to when speaking time runs out, so the limit sheet and the thing it offers instead
 * have to stay in step — if the alternatives ever became paid, the soft paywall would
 * quietly become a hard one.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clientUlid } from '@vibelang/api-client';
import {
  type FreeAlternative,
  PAYWALL_BENEFITS,
  type PaywallVariant,
  trialTimeline,
} from '@vibelang/core';
import { Banner, Button, ErrorState, LimitSheet, Paywall, Skeleton } from '@vibelang/ui';
import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useApi, useApp, useCourseId } from '../app-context.js';
import { qk, retryIf } from '../lib/client.js';
import { practiseWord } from '../lib/practise-word.js';
import { MistakeWorklist, PronunciationClinic } from './review.js';

export interface NavigationProps {
  readonly onNavigate: (route: string) => void;
}

// ── G1 Paywall ──────────────────────────────────────────────────────────────

/**
 * Three SKUs, yearly pre-selected with its real per-month figure. No countdown, no
 * discount against a price nobody paid, no weekly SKU. The competitor ships ten SKUs
 * including a weekly, and subscription confusion is a third of its negative reviews.
 */
export function PaywallScreen({
  variant,
  trigger,
  onDismiss,
}: {
  readonly variant: PaywallVariant;
  readonly trigger: string;
  readonly onDismiss: () => void;
}): JSX.Element {
  const api = useApi();
  const { track } = useApp();
  const [remind, setRemind] = useState(true);
  const shownAt = useRef(Date.now());

  // `plan_shown` is the hero plan, because that is the price the learner actually read.
  useEffect(() => {
    track('paywall_view', { trigger, variant, plan_shown: 'plus.yearly' });
  }, [trigger, variant, track]);

  const record = useMutation({
    mutationFn: (plan: string) =>
      api.call('recordPaywallEvent', {
        body: { id: clientUlid(), trigger, variant, outcome: 'cta', plan },
      }),
  });

  return (
    <Paywall
      variant={variant}
      trigger={trigger}
      headline="Unlimited speaking"
      // From the shared list, where every line is checked against the limit it claims.
      // This had "Every dialect" in it, which free already has, and listed the voice
      // cap twice.
      benefits={PAYWALL_BENEFITS.map((benefit) => benefit.title)}
      heroPlan={{
        id: 'plus.yearly',
        name: 'Plus, yearly',
        priceLabel: '$79.99 / year',
        // The real per-month figure, so the two plans are actually comparable rather
        // than rhetorically compared.
        perMonthLabel: '$6.67 / month',
        trialDays: 7,
      }}
      otherPlans={[
        {
          id: 'plus.monthly',
          name: 'Plus, monthly',
          priceLabel: '$14.99 / month',
          perMonthLabel: null,
          trialDays: 7,
        },
        {
          id: 'pro.yearly',
          name: 'Pro, yearly',
          priceLabel: '$149.99 / year',
          perMonthLabel: '$12.50 / month',
          trialDays: 7,
        },
      ]}
      rating={null}
      testimonial={null}
      remindBeforeTrial={remind}
      onToggleReminder={setRemind}
      onSelectPlan={(planId) => {
        track('paywall_cta', { plan: planId });
        record.mutate(planId);
      }}
      onViewOtherPlans={() => track('paywall_plans_expanded', {})}
      onDismiss={() => {
        // How long it was on screen separates "dismissed instantly" from "read it and
        // said no", which are different products problems with different answers.
        track('paywall_dismiss', { trigger, ms_visible: Date.now() - shownAt.current });
        onDismiss();
      }}
    />
  );
}

// ── G6 Limit sheet ──────────────────────────────────────────────────────────

/**
 * The alternatives are the point, and the type enforces it: `LimitSheet` takes a
 * non-empty tuple, so a limit screen with nothing free to offer does not compile.
 */
/**
 * A module constant rather than a local: the list never varies, and rebuilding it each
 * render would make the view event fire on every render instead of once per sheet.
 */
const VOICE_LIMIT_ALTERNATIVES: readonly [FreeAlternative, ...FreeAlternative[]] = [
  {
    kind: 'review',
    label: 'Clear your review queue — still scored',
    estimatedMinutes: 8,
    route: '/review',
  },
  {
    kind: 'text_mode',
    label: 'Work through your mistakes in writing',
    estimatedMinutes: 10,
    route: '/review/mistakes',
  },
  {
    kind: 'listen',
    label: "Listen to this week's drops",
    estimatedMinutes: 6,
    route: '/learn/listen',
  },
];

export function VoiceLimitSheet({
  resetsAt,
  onNavigate,
  onUpgrade,
  onDismiss,
}: NavigationProps & {
  readonly resetsAt: number;
  readonly onUpgrade: () => void;
  readonly onDismiss: () => void;
}): JSX.Element {
  const { track } = useApp();

  // The alternatives are logged by name, so a genuine alternative can be told from a
  // token one: if nobody ever taps an entry, it is decoration on a wall.
  useEffect(() => {
    track('limit_sheet_view', {
      capability: 'voice_minutes',
      alternatives: VOICE_LIMIT_ALTERNATIVES.map((alternative) => alternative.kind),
    });
  }, [track]);

  return (
    <LimitSheet
      limit="voice_minutes"
      resetsAt={resetsAt}
      alternatives={VOICE_LIMIT_ALTERNATIVES}
      upgradeHeadline="Plus removes the daily cap"
      onAlternative={(route) => {
        const taken = VOICE_LIMIT_ALTERNATIVES.find((alternative) => alternative.route === route);
        track('limit_alternative_tap', {
          capability: 'voice_minutes',
          alternative: taken?.kind ?? 'unknown',
        });
        onNavigate(route);
      }}
      onUpgrade={() => {
        track('limit_sheet_upgrade_tap', { capability: 'voice_minutes' });
        onUpgrade();
      }}
      onDismiss={onDismiss}
    />
  );
}

// ── E2 Mistakes (container) ─────────────────────────────────────────────────

/**
 * Supplies `MistakeWorklist` with data. The presentation lives in `review.tsx` and is
 * covered by its own tests; keeping the fetch out here means the screen stays testable
 * without a network.
 */
export function MistakesScreen({ onNavigate }: NavigationProps): JSX.Element {
  const { track } = useApp();
  const api = useApi();
  const courseId = useCourseId();
  const [status, setStatus] = useState<'open' | 'corrected'>('open');

  const query = useQuery({
    queryKey: qk.mistakes(courseId, status),
    queryFn: () => api.call('listMistakes', { query: { courseId, status, limit: 60 } }),
  });

  const practise = useMutation({
    mutationFn: (mistakeId: string) =>
      api.call('practiceMistake', { params: { mistakeId }, body: {} }),
    onSuccess: (drill) => onNavigate(`/learn/drill/${drill.id}`),
  });

  if (query.error !== null) {
    return (
      <ErrorState
        title="We couldn't load your mistakes"
        {...retryIf(query.error, () => void query.refetch())}
        alternative={{ label: 'Open review', onClick: () => onNavigate('/review') }}
      />
    );
  }

  return (
    <MistakeWorklist
      track={track}
      data={query.data ?? null}
      loading={query.isPending}
      status={status}
      onStatus={setStatus}
      onPractise={(mistakeId) => practise.mutate(mistakeId)}
      onNavigate={onNavigate}
    />
  );
}

// ── E3 Pronunciation clinic (container) ─────────────────────────────────────

export function ClinicScreen({
  onNavigate,
  phoneme,
}: NavigationProps & { readonly phoneme?: string | undefined }): JSX.Element {
  const { track } = useApp();
  const api = useApi();
  const courseId = useCourseId();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: qk.clinic(courseId),
    queryFn: () => api.call('getPronunciationClinic', { query: { courseId } }),
  });

  if (query.error !== null) {
    return (
      <ErrorState
        title="We couldn't load the clinic"
        {...retryIf(query.error, () => void query.refetch())}
        alternative={{ label: 'Open review', onClick: () => onNavigate('/review') }}
      />
    );
  }

  return (
    <PronunciationClinic
      track={track}
      data={query.data ?? null}
      loading={query.isPending}
      // Narrowed to one sound when the learner asked for one, which is what makes
      // "drill this sound" mean that rather than "here is the whole list again".
      {...(phoneme === undefined ? {} : { focus: phoneme })}
      onPractise={(next) => onNavigate(`/review/clinic/${encodeURIComponent(next)}`)}
      // E4's production practice. The clinic is refetched after a scored attempt because
      // the server may have just closed the mistake — the sound drops off the list and
      // the sparkline gains a point, and neither happens if the screen keeps its old copy.
      onPractiseAloud={async (word) => {
        const attempt = await practiseWord(api, { courseId, word });
        if (attempt.status === 'scored') {
          await queryClient.invalidateQueries({ queryKey: qk.clinic(courseId) });
        }
        return attempt;
      }}
      onNavigate={onNavigate}
    />
  );
}

// ── G4 Trial timeline sheet ─────────────────────────────────────────────────

/**
 * The dated schedule, shown *before* the store sheet.
 *
 * This one screen is the main defence against the refund-rage theme that accounts for
 * 17–23% of the competitor's negative reviews. Its whole job is to state the charge as
 * a date and an amount rather than as "3 days", because "3 days" is what people
 * misread and then feel tricked by. The timeline itself comes from
 * `trialTimeline` in `packages/core`, so the dates the learner is shown are the dates
 * the reminder scheduler actually uses.
 */
export function TrialTimelineSheet({
  productId,
  trialDays,
  priceLabel,
  onContinue,
  onBack,
}: {
  readonly productId: string;
  readonly trialDays: number;
  /** The store's own localised price string. We quote it; we do not compute it. */
  readonly priceLabel: string;
  readonly onContinue: () => void;
  readonly onBack: () => void;
}): JSX.Element {
  const { track } = useApp();

  useEffect(() => {
    track('trial_timeline_view', { product_id: productId, trial_days: trialDays });
  }, [productId, trialDays, track]);

  const entries = useMemo(
    () =>
      trialTimeline(Date.now(), trialDays, priceLabel, (ms) =>
        new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'long' }),
      ),
    [trialDays, priceLabel],
  );

  return (
    <section className="vl-sheet vl-trial" aria-labelledby="vl-trial-title">
      <h2 className="vl-sheet__title" id="vl-trial-title">
        Here is exactly what happens
      </h2>

      <ol className="vl-trial__timeline">
        {entries.map((entry) => (
          <li key={entry.kind} className="vl-trial__entry" data-kind={entry.kind}>
            <span className="vl-trial__date">
              {new Date(entry.at).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'long',
              })}
            </span>
            <span className="vl-trial__label">{entry.label}</span>
          </li>
        ))}
      </ol>

      {/* Stated twice on purpose: once in the timeline and once as a sentence. The
          competitor's reviews are full of people who say they never saw it. */}
      <p className="vl-trial__restate">
        You will not be charged today, and we will remind you before the last date — by email and by
        notification. Cancel any time before it and you pay nothing.
      </p>

      <div className="vl-step__actions">
        <Button
          onClick={() => {
            track('trial_timeline_continue', { product_id: productId });
            onContinue();
          }}
        >
          Continue
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            // Going back is recorded as deliberately as going forward: this screen is
            // meant to let people stop, and a version nobody backs out of is a version
            // nobody is reading.
            track('trial_timeline_back', {});
            onBack();
          }}
        >
          Not yet
        </Button>
      </div>
    </section>
  );
}

// ── G2 Plan comparison ──────────────────────────────────────────────────────

/** A row of the comparison table. `null` means "not included", not "unknown". */
interface PlanRow {
  readonly feature: string;
  readonly free: string | null;
  readonly plusMonthly: string | null;
  readonly plusAnnual: string | null;
  readonly pro: string | null;
}

/**
 * The Free column is fully populated on purpose.
 *
 * A comparison table that leaves the free column empty is making a threat; one that
 * fills it in is making a claim it has to stand behind. Everything listed as free here
 * is free forever, which is the wedge the whole product is built on.
 */
const PLAN_ROWS: readonly PlanRow[] = [
  {
    feature: 'Scored speaking',
    free: '10 minutes a day',
    plusMonthly: 'Unlimited',
    plusAnnual: 'Unlimited',
    pro: 'Unlimited',
  },
  {
    feature: 'Review and mistakes',
    free: 'Everything',
    plusMonthly: 'Everything',
    plusAnnual: 'Everything',
    pro: 'Everything',
  },
  { feature: 'Scenarios', free: 'Starter set', plusMonthly: 'All', plusAnnual: 'All', pro: 'All' },
  {
    feature: 'Analysis depth',
    free: 'Per-turn scores',
    plusMonthly: 'Per-phoneme',
    plusAnnual: 'Per-phoneme',
    pro: 'Per-phoneme + trends',
  },
  {
    feature: 'Report history',
    free: '7 days',
    plusMonthly: 'Everything',
    plusAnnual: 'Everything',
    pro: 'Everything',
  },
  { feature: 'Offline lessons', free: null, plusMonthly: 'Yes', plusAnnual: 'Yes', pro: 'Yes' },
  /*
   * These three said Plus *annual* got "All" languages, exam preparation and the monthly
   * assessment. It does not, and never did: `resolveEntitlement` reads `LIMITS[tier]`
   * and the period — monthly or annual — never touches the limits. A `plus_annual`
   * subscriber resolves to `plus`, whose `examTracks` and `monthlyAssessment` are both
   * false and whose `simultaneousCourses` is 1, the same as free.
   *
   * So this table promised two Pro-only capabilities to someone paying $79.99 a year.
   * iOS and Android had it right all along — their tables have one Plus column, because
   * that is how many the entitlement model has. `08-monetization` §2 agrees with the
   * code. The four-column shape here invented a split that does not exist, and the two
   * invented cells were filled in with a guess.
   */
  {
    feature: 'Simultaneous courses',
    free: 'One',
    plusMonthly: 'One',
    plusAnnual: 'One',
    pro: 'Two',
  },
  /*
   * Not "Yes". `exam_target` is stored on the course and read by nothing — no content
   * selection, no rubric, no screen — and `11-roadmap` puts exam tracks GA at month 7+.
   * A comparison table is where someone decides to pay, so a row that is coming says
   * so, the way the games row names the two games that are not built rather than
   * opening a screen that counts nothing.
   */
  { feature: 'Exam preparation', free: null, plusMonthly: null, plusAnnual: null, pro: 'Later' },
  { feature: 'Monthly assessment', free: null, plusMonthly: null, plusAnnual: null, pro: 'Yes' },
  { feature: 'Priority voices', free: null, plusMonthly: null, plusAnnual: null, pro: 'Yes' },
];

const PLAN_COLUMNS: readonly {
  readonly id: string;
  readonly label: string;
  readonly key: keyof Omit<PlanRow, 'feature'>;
}[] = [
  { id: 'free', label: 'Free', key: 'free' },
  { id: 'plus.monthly', label: 'Plus · monthly', key: 'plusMonthly' },
  { id: 'plus.yearly', label: 'Plus · annual', key: 'plusAnnual' },
  { id: 'pro.yearly', label: 'Pro', key: 'pro' },
];

export function PlanComparisonScreen({
  source,
  onSelect,
}: {
  /** Where the learner came from, so the table's own conversion can be attributed. */
  readonly source: string;
  readonly onSelect: (productId: string, period: 'monthly' | 'yearly' | 'free') => void;
}): JSX.Element {
  const api = useApi();
  const { track } = useApp();

  const entitlements = useQuery({
    queryKey: qk.entitlements,
    queryFn: () => api.call('getEntitlements', {}),
  });

  const tier = entitlements.data?.entitlement.tier;
  useEffect(() => {
    if (tier === undefined) return;
    track('plan_comparison_view', { source, current_tier: tier });
  }, [source, tier, track]);

  return (
    <main id="main" className="vl-page vl-plans">
      <h1>What each plan includes</h1>

      {entitlements.isPending && <Skeleton lines={6} label="Loading your plan" />}

      <table className="vl-plans__table">
        <thead>
          <tr>
            <th scope="col">Feature</th>
            {PLAN_COLUMNS.map((column) => (
              <th
                key={column.id}
                scope="col"
                // The learner's own plan is marked rather than hidden, so the table
                // answers "what do I have" as well as "what could I have".
                data-current={tier !== undefined && column.id.startsWith(tier) ? 'true' : 'false'}
              >
                {column.label}
                {tier !== undefined && column.id.startsWith(tier) && (
                  <span className="vl-plans__current"> · your plan</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PLAN_ROWS.map((row) => (
            <tr key={row.feature}>
              <th scope="row">{row.feature}</th>
              {PLAN_COLUMNS.map((column) => (
                <td key={column.id}>
                  {row[column.key] ?? <span className="vl-plans__absent">—</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="vl-step__actions">
        {PLAN_COLUMNS.filter((column) => column.id !== 'free').map((column) => (
          <Button
            key={column.id}
            variant={column.id === 'plus.yearly' ? 'primary' : 'secondary'}
            onClick={() => {
              const period = column.id.endsWith('monthly') ? 'monthly' : 'yearly';
              track('plan_selected', { product_id: column.id, period });
              onSelect(column.id, period);
            }}
          >
            Choose {column.label}
          </Button>
        ))}
      </div>

      {/*
        The spec's `prices unavailable` state, said out loud rather than left as an
        absence. There is no store on web, so there is no localised price to show — and
        printing the catalogue USD figure is the mistake iOS already made once, showing
        `$79.99` to a learner billed in euros on the one screen whose job is to state the
        amount correctly (`PaywallPolicy.swift`). A comparison table with no cost column
        and no explanation leaves the learner unable to tell whether that is the design
        or a bug.
      */}
      <p className="vl-note">
        Prices are shown at checkout, in your own currency and by your app store — so the figure you
        see is the figure you are charged.
      </p>

      {/* The free tier is a choice here, not an absence of one. */}
      <p className="vl-note">
        Staying on Free is a real option. Ten minutes of scored speaking a day, review, mistakes and
        your progress do not expire.
      </p>
    </main>
  );
}

// ── G3 Contextual paywall ───────────────────────────────────────────────────

/**
 * Feature-specific, and it names the capability the learner just reached for.
 *
 * The difference between this and G1 is that this one is about one thing, so it can be
 * honest about one thing. `Not now` returns the learner exactly where they were, and
 * the dismissal is recorded as deliberately as the conversion — a soft paywall is only
 * soft if we measure the people who say no.
 */
export function ContextualPaywall({
  capability,
  trigger,
  headline,
  detail,
  productId,
  onConvert,
  onDismiss,
}: {
  readonly capability: string;
  readonly trigger: string;
  readonly headline: string;
  readonly detail: string;
  readonly productId: string;
  readonly onConvert: (productId: string) => void;
  readonly onDismiss: () => void;
}): JSX.Element {
  const { track } = useApp();

  useEffect(() => {
    track('contextual_paywall_view', { capability, trigger });
  }, [capability, trigger, track]);

  return (
    <section className="vl-sheet vl-contextual" aria-labelledby="vl-contextual-title">
      <h2 className="vl-sheet__title" id="vl-contextual-title">
        {headline}
      </h2>
      <p className="vl-sheet__body">{detail}</p>

      <div className="vl-step__actions">
        <Button
          onClick={() => {
            track('contextual_paywall_convert', { capability, product_id: productId });
            onConvert(productId);
          }}
        >
          Unlock it
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            track('contextual_paywall_dismiss', { capability });
            onDismiss();
          }}
        >
          Not now
        </Button>
      </div>
    </section>
  );
}

// ── G5b Dismissal offer ─────────────────────────────────────────────────────

/**
 * What a dismissed paywall says next.
 *
 * One free lesson, no card, no countdown. A learner who has just said no to a price is
 * the one most worth handing the product to; Loora's version of this is the
 * highest-scoring dismissal handling in the category
 * (docs/research/07-live-refresh-2026-10-18.md §2).
 *
 * Once, and a decline is a real answer — there is no second ask.
 */
export function DismissalOffer({
  onAccept,
  onDecline,
}: {
  /** Called once the grant has actually landed, so the promised lesson is possible. */
  readonly onAccept: () => void;
  readonly onDecline: () => void;
}): JSX.Element {
  const { track } = useApp();
  const api = useApi();

  /**
   * The accept path used to route straight to a lesson, which made the copy false for
   * exactly the learner it was written for: someone who has just hit the voice limit has
   * no minutes left, so "a full scored lesson, free" was an offer the quota refused.
   *
   * Loora's version grants a lesson (docs/research/16-category-leaders-2026-10-20.md §2).
   * Ours now grants the minutes a lesson needs, server-side, before routing anywhere —
   * and routes regardless of the answer, because a learner who already has minutes gets
   * `granted: false` and should still get the lesson they just accepted.
   */
  const claim = useMutation({
    mutationFn: () => api.call('claimDismissalGift', { body: { trigger: 'dismissal_offer' } }),
    onSettled: onAccept,
  });

  return (
    <section className="vl-sheet vl-dismissal" aria-labelledby="vl-dismissal-title">
      <h2 className="vl-sheet__title" id="vl-dismissal-title">
        Have one on us
      </h2>
      <p>
        Five more voice minutes, free, with no card and nothing to cancel — enough for a full scored
        lesson. They&apos;re yours for the rest of today. If it is not for you, that is a fair
        answer and we will not ask again today.
      </p>
      <div className="vl-sheet__actions">
        <Button
          disabled={claim.isPending}
          onClick={() => {
            track('paywall_dismiss_offer', { accepted: true });
            claim.mutate();
          }}
        >
          Take the free lesson
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            track('paywall_dismiss_offer', { accepted: false });
            onDecline();
          }}
        >
          No thanks
        </Button>
      </div>
    </section>
  );
}

// ── G5 Post-subscription welcome ────────────────────────────────────────────

/**
 * Shown once, immediately after a purchase, and it exists to lower the
 * immediate-refund rate.
 *
 * The entitlement often has not propagated yet when this appears. Saying so — and
 * saying what *is* already available — is the difference between a learner who waits
 * thirty seconds and a learner who asks for their money back because the thing they
 * just bought appears not to work.
 */
export function SubscriptionWelcome({
  tier,
  period,
  renewsAt,
  entitlementReady,
  onStart,
  onDismiss,
}: {
  readonly tier: string;
  readonly period: 'monthly' | 'yearly';
  readonly renewsAt: number | null;
  readonly entitlementReady: boolean;
  readonly onStart: () => void;
  readonly onDismiss: () => void;
}): JSX.Element {
  const { track } = useApp();

  useEffect(() => {
    track('subscription_welcome_view', { tier, period });
  }, [tier, period, track]);

  const unlocked = [
    'No daily cap on scored speaking',
    'Every scenario, including the locked rails',
    'Per-phoneme analysis on every turn',
    'Your full report-card history, back to your first lesson',
  ];

  return (
    <section className="vl-sheet vl-welcome" aria-labelledby="vl-welcome-title">
      <h2 className="vl-sheet__title" id="vl-welcome-title">
        You&apos;re on {tier.replace(/^./, (c) => c.toUpperCase())}
      </h2>

      {!entitlementReady && (
        <Banner tone="info">
          The store is still confirming with us — usually a few seconds. Speaking, review and your
          mistakes all work right now; the unlocked scenarios appear as soon as it lands.
        </Banner>
      )}

      <ul className="vl-welcome__list">
        {unlocked.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>

      {/* The renewal date, again. It was on the trial timeline and it is here, because
          "I did not know when it renewed" is the single most common billing complaint
          in the competitor's reviews. */}
      {renewsAt !== null && (
        <p className="vl-note">
          Renews{' '}
          {new Date(renewsAt).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
          . You can cancel any time from Settings, one tap in.
        </p>
      )}

      <div className="vl-step__actions">
        <Button onClick={onStart}>Start a scenario</Button>
        <Button
          variant="secondary"
          onClick={() => {
            // Whether the entitlement had landed by the time they left is the number
            // that says if the propagation delay is costing us refunds.
            track('subscription_welcome_dismiss', { entitlement_ready: entitlementReady });
            onDismiss();
          }}
        >
          Later
        </Button>
      </div>
    </section>
  );
}

// ── G7 Win-back ─────────────────────────────────────────────────────────────

/**
 * Shown once in the cancel flow, with a real offer stated plainly and no second
 * attempt if it is declined.
 *
 * "Once" is the whole design. A win-back that asks twice is the thing that turns a
 * lapsed subscriber into a one-star review about being nagged, and the competitor's
 * reviews have that complaint in them.
 */
export function WinbackSheet({
  lapsedDays,
  offer,
  onAccept,
  onDecline,
}: {
  readonly lapsedDays: number;
  /** A real discount or a free month, named. Never a vague "special offer". */
  readonly offer: string;
  readonly onAccept: (offer: string) => void;
  readonly onDecline: () => void;
}): JSX.Element {
  const { track } = useApp();

  useEffect(() => {
    track('winback_view', { lapsed_days: lapsedDays, offer });
  }, [lapsedDays, offer, track]);

  return (
    <section className="vl-sheet vl-winback" aria-labelledby="vl-winback-title">
      <h2 className="vl-sheet__title" id="vl-winback-title">
        Before you go
      </h2>
      <p className="vl-sheet__body">{offer}</p>

      {/* Said out loud, because it is the thing that makes the offer trustworthy. */}
      <p className="vl-note">
        We will not ask again. If you say no, cancelling goes through and your free account keeps
        everything you have done.
      </p>

      <div className="vl-step__actions">
        <Button
          onClick={() => {
            track('winback_convert', { offer });
            onAccept(offer);
          }}
        >
          Take the offer
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            track('winback_dismiss', {});
            onDecline();
          }}
        >
          No thanks, cancel
        </Button>
      </div>
    </section>
  );
}
